import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Dialog para editar montadores asignados + vehículo en una ruta existente.
 * Funciona en cualquier estado (planificada, en_curso, finalizada): permite
 * reasignar mid-route en caso de incidencia.
 */
export default function EditMontadoresVehiculoDialog({ open, onClose, ruta, onSaved }) {
  const [users, setUsers] = useState([]);
  const [externos, setExternos] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [montadores, setMontadores] = useState([]);
  const [vehiculoId, setVehiculoId] = useState("");
  const [vehiculoPropio, setVehiculoPropio] = useState(false);
  const [fecha, setFecha] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMontadores((ruta?.montadores || []).map((m) => ({
      tipo: m.usuario_id ? "interno" : "externo",
      id: m.usuario_id || m.externo_id || "",
      rol: m.rol || "principal",
    })));
    setVehiculoId(ruta?.vehiculo_id || "");
    setVehiculoPropio(!!ruta?.vehiculo_propio);
    setFecha(ruta?.fecha || "");
    (async () => {
      try {
        const [u, e, v] = await Promise.all([
          api.get("/users"),
          api.get("/colaboradores"),
          api.get("/vehiculos"),
        ]);
        setUsers(u.data || []);
        setExternos(e.data || []);
        setVehiculos(v.data || []);
      } catch (err) {
        toast.error("Error cargando datos: " + (err?.response?.data?.detail || err.message));
      }
    })();
  }, [open, ruta]);

  const addM = (tipo) => setMontadores((arr) => [...arr, { tipo, id: "", rol: "principal" }]);
  const updateM = (i, patch) => setMontadores((arr) => arr.map((m, idx) => idx === i ? { ...m, ...patch } : m));
  const removeM = (i) => setMontadores((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    // Validar
    const invalid = montadores.find((m) => !m.id);
    if (invalid) {
      toast.error("Hay montadores sin seleccionar");
      return;
    }

    // Construir payload
    const payload_montadores = montadores.map((m) => {
      const lista = m.tipo === "interno" ? users : externos;
      const obj = lista.find((x) => x.id === m.id);
      const nombre_visible = obj?.name || obj?.nombre || obj?.email || "—";
      return m.tipo === "interno"
        ? { usuario_id: m.id, nombre_visible, rol: m.rol }
        : { externo_id: m.id, nombre_visible, rol: m.rol };
    });

    setSaving(true);
    try {
      await api.patch(`/rutas/${ruta.id}`, {
        montadores: payload_montadores,
        vehiculo_id: vehiculoPropio ? null : (vehiculoId || null),
        vehiculo_propio: vehiculoPropio,
        ...(fecha && fecha !== ruta.fecha ? { fecha } : {}),
      });
      toast.success("Ruta actualizada");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (!ruta) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl" data-testid="dialog-edit-ruta-asignacion">
        <DialogHeader>
          <DialogTitle className="text-zinc-100">Editar asignación de ruta · {ruta.codigo}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Fecha */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3">
            <Label className="text-xs text-zinc-400 mb-1.5 block">📅 Fecha de la ruta</Label>
            <input type="date" value={fecha || ""}
              onChange={(e) => setFecha(e.target.value)}
              data-testid="edit-ruta-fecha"
              className="bg-zinc-950 border border-zinc-800 rounded h-8 px-2 text-sm text-zinc-100 w-full" />
            {fecha && fecha !== ruta.fecha && (
              <div className="text-[11px] text-amber-300 mt-1.5">
                ⚠️ Cambias la fecha de {ruta.fecha} → <b>{fecha}</b>. Los montadores asignados verán la ruta en la nueva fecha.
              </div>
            )}
          </div>

          {/* Vehículo */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3">
            <Label className="text-xs text-zinc-400 mb-1.5 block">🚐 Vehículo</Label>
            <Select
              value={vehiculoPropio ? "propio" : (vehiculoId || "ninguno")}
              onValueChange={(v) => {
                if (v === "propio") { setVehiculoPropio(true); setVehiculoId(""); }
                else { setVehiculoPropio(false); setVehiculoId(v === "ninguno" ? "" : v); }
              }}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 h-8 text-sm" data-testid="edit-ruta-vehiculo">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="ninguno">Sin asignar</SelectItem>
                <SelectItem value="propio">🚐 Vehículo propio del montador (autónomo)</SelectItem>
                {vehiculos.filter((v) => v.activo !== false).map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.nombre} {v.matricula ? `(${v.matricula})` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Montadores */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-zinc-400">👷 Montadores asignados</Label>
              <div className="flex gap-1">
                <Button type="button" size="sm" variant="ghost" onClick={() => addM("interno")}
                  className="h-7 px-2 text-xs text-amber-300 hover:text-amber-200" data-testid="edit-add-montador-interno">
                  <Plus className="w-3 h-3 mr-1" /> DNF
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => addM("externo")}
                  className="h-7 px-2 text-xs text-violet-300 hover:text-violet-200" data-testid="edit-add-montador-externo">
                  <Plus className="w-3 h-3 mr-1" /> Externo
                </Button>
              </div>
            </div>

            {montadores.length === 0 && (
              <div className="text-xs text-zinc-500 italic">Sin montadores. Añade al menos uno.</div>
            )}
            <div className="space-y-1.5">
              {montadores.map((m, idx) => {
                const lista = m.tipo === "interno"
                  ? users.filter((u) => (u.roles || []).includes("montador") || (u.roles || []).includes("admin"))
                  : externos;
                return (
                  <div key={idx} className="grid grid-cols-[80px_1fr_120px_auto] gap-2 items-center">
                    <span className={`text-[10px] uppercase tracking-wider text-center px-1.5 py-0.5 rounded ${m.tipo === "interno" ? "bg-amber-500/20 text-amber-300" : "bg-violet-500/20 text-violet-300"}`}>
                      {m.tipo === "interno" ? "DNF" : "Externo"}
                    </span>
                    <Select value={m.id || ""} onValueChange={(v) => updateM(idx, { id: v })}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800 h-8 text-xs" data-testid={`edit-montador-sel-${idx}`}>
                        <SelectValue placeholder="Selecciona…" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[260px]">
                        {lista.map((x) => (
                          <SelectItem key={x.id} value={x.id}>{x.name || x.nombre || x.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={m.rol} onValueChange={(v) => updateM(idx, { rol: v })}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                        <SelectItem value="principal">Principal</SelectItem>
                        <SelectItem value="ayudante">Ayudante</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeM(idx)}
                      className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400" data-testid={`edit-remove-montador-${idx}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          {ruta.estado !== "planificada" && (
            <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2">
              ⚠️ Ruta en estado <b>{ruta.estado}</b>. Cambiar los montadores/vehículo no afecta a las paradas ya cerradas, pero sí permite que el nuevo montador pueda reportar las paradas restantes.
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold" data-testid="edit-ruta-guardar">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              Guardar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
